import { useEffect, useState, type RefObject } from 'react';

import { OVERLAY_HEADER_HEIGHT } from '../components';

import type { NavSection } from './navConfig';

export function useActiveSection(
  sections: NavSection[],
  scrollContainerRef: RefObject<HTMLDivElement>
): string {
  const [activeHref, setActiveHref] = useState('');

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const uniqueHrefs = Array.from(
      new Set(
        sections.flatMap((section) => [
          section.href,
          ...(section.children?.map((child) => child.href) ?? []),
        ])
      )
    );

    const ratios = new Map<string, number>();
    const observed = new Set<Element>();

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = `#${entry.target.id}`;
          if (entry.isIntersecting) {
            ratios.set(id, entry.intersectionRatio);
          } else {
            ratios.delete(id);
          }
        });
        let bestHref = '';
        let bestRatio = -1;
        for (const href of uniqueHrefs) {
          const ratio = ratios.get(href) ?? -1;
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestHref = href;
          }
        }
        setActiveHref(bestRatio >= 0 ? bestHref : '');
      },
      {
        root: container,
        rootMargin: `-${OVERLAY_HEADER_HEIGHT}px 0px -70% 0px`,
        threshold: [0, 0.5, 1],
      }
    );

    const observeNewElements = () => {
      uniqueHrefs.forEach((href) => {
        const el = container.querySelector(href);
        if (el && !observed.has(el)) {
          observed.add(el);
          intersectionObserver.observe(el);
        }
      });
    };

    observeNewElements();

    if (observed.size < uniqueHrefs.length) {
      // Some sections not in DOM yet (async load) — watch for them
      const mutationObserver = new MutationObserver(() => {
        observeNewElements();
        if (observed.size >= uniqueHrefs.length) {
          mutationObserver.disconnect();
        }
      });
      mutationObserver.observe(container, { childList: true, subtree: true });

      return () => {
        mutationObserver.disconnect();
        intersectionObserver.disconnect();
      };
    }

    return () => intersectionObserver.disconnect();
  }, [sections, scrollContainerRef]);

  return activeHref;
}
